import React, { useState, useEffect } from "react";
import axios from "axios";
import "../App.css";
import { Link, useNavigate } from "react-router-dom";
import { SignedIn, SignedOut, SignIn, useClerk, useUser, useAuth } from "@clerk/clerk-react";

const clerkAppearance = {
// ... existing clerkAppearance ...
  variables: {
    colorPrimary: "#007bff",
    colorText: "white",
    colorBackground: "#2a2a2a",
    colorInputBackground: "#333",
    colorInputText: "white",
  },
  elements: {
    card: {
      backgroundColor: "#2a2a2a",
      border: "1px solid #444",
      borderRadius: "15px",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
    },
    headerTitle: { color: "white" },
    headerSubtitle: { color: "#ccc" },
    socialButtonsBlockButton: {
      backgroundColor: "#333",
      border: "1px solid #444",
      "&:hover": { backgroundColor: "#444" },
    },
    socialButtonsBlockButtonText: { color: "white" },
    dividerLine: { backgroundColor: "#444" },
    dividerText: { color: "#888" },
    formFieldLabel: { color: "#ccc" },
    formFieldInput: {
      backgroundColor: "#333",
      border: "1px solid #555",
      color: "white",
    },
    footerActionText: { color: "#888" },
    footerActionLink: {
      color: "#007bff",
      "&:hover": { color: "#0056b3" },
    },
    identityPreviewText: { color: "white" },
    identityPreviewEditButtonIcon: { color: "#007bff" },
  },
};

const API_URL = (process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '')) + '/api';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { user } = useUser();
  const { getToken } = useAuth();
  const [schoolName, setSchoolName] = useState("");
  const [tempSchoolName, setTempSchoolName] = useState("");
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [allAccounts, setAllAccounts] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [allFeedback, setAllFeedback] = useState([]);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showSuperAdminFeedback, setShowSuperAdminFeedback] = useState(false);

  const isSuperAdmin = user?.primaryEmailAddress?.emailAddress === "slimsoftit@hotmail.com";

  useEffect(() => {
    const checkUserStatus = async () => {
      if (!user) return;
      
      console.log("Checking status for user.id:", user.id);
      const email = user.primaryEmailAddress?.emailAddress;
      if (email === "slimsoftit@hotmail.com") {
        console.log("Super Admin detected via email");
        setPaymentStatus('active');
        setSchoolName("Super Admin");
        return;
      }
// ... rest of useEffect ...

      try {
        const response = await axios.get(`${API_URL}/get-user-status/${user.id}?email=${encodeURIComponent(email)}`);
        console.log("Backend response for status:", response.data);
        if (response.data.success) {
          console.log("Setting payment status to:", response.data.status);
          setPaymentStatus(response.data.status);
          if (response.data.rijschool_naam) {
            setSchoolName(response.data.rijschool_naam);
          }
        } else {
          console.log("User not found in DB, setting status to unregistered");
          setPaymentStatus('unregistered');
        }
      } catch (error) {
        console.error("Error checking status:", error);
      }
    };
    
    checkUserStatus();
  }, [user]);

  const handlePayment = async () => {
    try {
      const token = await getToken();
      const response = await axios.post(`${API_URL}/create-checkout-session`, {
        clerkId: user.id,
        email: user.primaryEmailAddress.emailAddress
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      alert("Er is iets misgegaan bij het starten van de betaling.");
    }
  };

  useEffect(() => {
    if (isSuperAdmin && schoolName === "Super Admin") {
      fetchAccounts();
    }
  }, [isSuperAdmin, schoolName]);

  const fetchAccounts = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${API_URL}/admin-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  const fetchFeedback = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${API_URL}/platform-feedback`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllFeedback(response.data);
      setShowSuperAdminFeedback(true);
    } catch (error) {
      console.error("Error fetching feedback:", error);
      const message = error.response?.data?.error || "Fout bij ophalen feedback.";
      alert(message);
    }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;
    
    setIsSubmittingFeedback(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/platform-feedback`, {
        feedback,
        rijschoolNaam: schoolName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Bedankt voor je feedback! We gaan ermee aan de slag.");
      setFeedback("");
      setShowFeedbackModal(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      const message = error.response?.data?.error || "Er ging iets mis bij het versturen van je feedback.";
      alert(message);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const deleteAccount = async (id) => {
    if (window.confirm("Weet u zeker dat u dit account wilt verwijderen?")) {
      try {
        const token = await getToken();
        await axios.delete(`${API_URL}/admin-accounts/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchAccounts();
      } catch (error) {
        console.error("Error deleting account:", error);
        alert("Fout bij het verwijderen van het account.");
      }
    }
  };

  const handleLogout = async () => {
    await signOut();
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminRijschool');
    setSchoolName("");
    navigate('/');
  };

  const handleSyncSchool = async (e) => {
    e.preventDefault();
    setIsSyncing(true);
    
    const email = user?.primaryEmailAddress?.emailAddress;
    if (!email) {
      alert("Fout: Geen email adres gevonden.");
      setIsSyncing(false);
      return;
    }

    try {
      const token = await getToken();
      const response = await axios.post(`${API_URL}/sync-rijschool`, {
        email: email,
        rijschoolNaam: tempSchoolName,
        clerkId: user.id,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSchoolName(tempSchoolName);
      }
    } catch (error) {
      alert(`Er is een fout opgetreden: ${error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="main-content-wrapper fade-in-up">
      {!isSuperAdmin && (
        <div className="top-left-fixed">
          <button onClick={() => navigate('/')} className="action-button-blue">Home</button>
        </div>
      )}

      <SignedIn>
        {!isSuperAdmin && (
          <div className="top-right-fixed">
            <button onClick={handleLogout} className="action-button-red">Uitloggen</button>
          </div>
        )}

        {isLoadingProfile ? (
          <div className="admin-card">
            <p>Profiel laden...</p>
          </div>
        ) : paymentStatus !== 'active' ? (
          <div className="admin-card" style={{ maxWidth: '500px', padding: '40px', textAlign: 'center' }}>
            <h2 className="aharoni-style">Abonnement Vereist</h2>
            <p>Om gebruik te maken van PlanSucces is een actief abonnement nodig.</p>
            <div style={{ margin: '30px 0', padding: '20px', background: '#333', borderRadius: '10px' }}>
              <h3 style={{ margin: '0 0 10px 0' }}>Instructeurs Pakket</h3>
              <p style={{ fontSize: '2em', fontWeight: 'bold', margin: '10px 0' }}>€5.00 <span style={{ fontSize: '0.5em', fontWeight: 'normal' }}>/ maand</span></p>
              <ul style={{ textAlign: 'left', listStyle: 'none', padding: '0', margin: '20px 0' }}>
                <li>✓ Alle beheerfuncties</li>
                <li>✓ Onbeperkt afspraken</li>
                <li>✓ Directe ondersteuning</li>
              </ul>
            </div>
            <button onClick={handlePayment} className="btn" style={{ width: '100%', minWidth: 'auto' }}>
              Nu Activeren & Betalen
            </button>
            <p style={{ fontSize: '0.8em', color: '#888', marginTop: '20px' }}>
              Na betaling kunt u uw rijschool registreren.
            </p>
          </div>
        ) : !schoolName ? (
          <div className="admin-card" style={{ maxWidth: '500px', padding: '40px' }}>
            <h3>Bijna klaar!</h3>
            <p>Voer de naam van uw rijschool in om uw account te koppelen.</p>
            <form onSubmit={handleSyncSchool} className="form-container" style={{ maxWidth: 'none', width: '100%' }}>
              <label className="form-label">Rijschool Naam:</label>
              <input 
                type="text" 
                className="form-input" 
                value={tempSchoolName} 
                onChange={(e) => setTempSchoolName(e.target.value)}
                placeholder="Bijv. Rijschool De Vries"
                required 
              />
              <button type="submit" className="btn" disabled={isSyncing} style={{ width: '100%', minWidth: 'auto' }}>
                {isSyncing ? "Koppelen..." : "Rijschool Registreren"}
              </button>
            </form>
          </div>
        ) : (
          <div className="admin-dashboard fade-in-up">
            <div className="welcome-banner">
              <h2>Welkom terug, {schoolName}!</h2>
              <p>Fijn om je weer te zien. Wat wil je vandaag doen?</p>
            </div>
            
            <div className="admin-grid">
              {isSuperAdmin && (
                <>
                  <div className="super-admin-controls">
                    <button onClick={() => navigate('/')} className="action-button-blue custom-admin-pos-1">Home</button>
                    <button onClick={handleLogout} className="action-button-red custom-admin-pos-2">Uitloggen</button>
                  </div>
                  
                  <div className="admin-card" style={{ gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h3>{showSuperAdminFeedback ? "Feedback Overzicht" : "Account Management"} (Super Admin)</h3>
                      <button 
                        onClick={() => showSuperAdminFeedback ? setShowSuperAdminFeedback(false) : fetchFeedback()}
                        className="btn" 
                        style={{ minWidth: 'auto', padding: '8px 16px' }}
                      >
                        {showSuperAdminFeedback ? "Terug naar Accounts" : "Feedback Inzien"}
                      </button>
                    </div>

                    <div style={{ width: '100%' }}>
                      {!showSuperAdminFeedback ? (
                        <table style={{ width: '100%', color: 'white', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #444' }}>
                              <th style={{ padding: '10px', textAlign: 'left' }}>Email</th>
                              <th style={{ padding: '10px', textAlign: 'left' }}>Rijschool</th>
                              <th style={{ padding: '10px', textAlign: 'right' }}>Actie</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allAccounts.map(acc => (
                              <tr key={acc.id} style={{ borderBottom: '1px solid #333' }}>
                                <td style={{ padding: '10px' }}>{acc.email}</td>
                                <td style={{ padding: '10px' }}>{acc.rijschool_naam}</td>
                                <td style={{ padding: '10px', textAlign: 'right' }}>
                                  <button 
                                    onClick={() => deleteAccount(acc.id)}
                                    className="action-button-red"
                                    style={{ padding: '4px 12px', fontSize: '12px' }}
                                  >
                                    Verwijderen
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="feedback-list">
                          {allFeedback.length === 0 ? (
                            <p>Geen feedback gevonden.</p>
                          ) : (
                            allFeedback.map(fb => (
                              <div key={fb.id} style={{ background: '#333', padding: '15px', borderRadius: '10px', marginBottom: '10px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                  <strong style={{ color: '#007bff' }}>{fb.rijschool_naam}</strong>
                                  <span style={{ fontSize: '0.8em', color: '#888' }}>{new Date(fb.datum).toLocaleString('nl-NL')}</span>
                                </div>
                                <p style={{ margin: 0 }}>{fb.feedback}</p>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div className="admin-card">
                <h3>Afspraken Beheren</h3>
                <p>Bekijk, bewerk of verwijder de gemaakte lesreserveringen.</p>
                <Link to="/AdminAppointments" className="btn" style={{ minWidth: 'auto', width: '100%' }}>
                  Bekijk Afspraken
                </Link>
              </div>

              <div className="admin-card">
                <h3>Feedback & Ideeën</h3>
                <p>Help ons PlanSucces te verbeteren. Deel je suggesties of wensen!</p>
                <button 
                  onClick={() => setShowFeedbackModal(true)} 
                  className="btn" 
                  style={{ minWidth: 'auto', width: '100%', background: 'linear-gradient(135-deg, #28a745, #218838)' }}
                >
                  Feedback Geven
                </button>
              </div>

              <div className="admin-card">
                <h3>Instellingen</h3>
                <p>Pas je rijschool gegevens of account instellingen aan.</p>
                <button className="btn" style={{ minWidth: 'auto', width: '100%', opacity: 0.7, cursor: 'not-allowed' }}>
                  Binnenkort beschikbaar
                </button>
              </div>

              <div className="admin-card">
                <h3>Account</h3>
                <p>Uw accountinstellingen en betalingen beheren via Stripe.</p>
                <a 
                  href="https://billing.stripe.com/p/login/7sYaEZa8zaf5eLT1k4aR200" 
                  className="btn" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ minWidth: 'auto', width: '100%', display: 'inline-block', textDecoration: 'none', textAlign: 'center' }}
                >
                  Account Beheren
                </a>
              </div>
            </div>

            {/* Feedback Modal */}
            {showFeedbackModal && (
              <div className="modal-overlay">
                <div className="admin-card" style={{ maxWidth: '500px', width: '90%', position: 'relative' }}>
                  <button 
                    onClick={() => setShowFeedbackModal(false)}
                    style={{ position: 'absolute', top: '10px', right: '15px', background: 'none', border: 'none', color: 'white', fontSize: '1.5em', cursor: 'pointer' }}
                  >
                    ×
                  </button>
                  <h3>Feedback Deel je ideeën!</h3>
                  <p>Mis je een functie of heb je een suggestie? Laat het ons weten.</p>
                  <form onSubmit={submitFeedback}>
                    <textarea 
                      className="form-input" 
                      style={{ width: '100%', minHeight: '150px', marginBottom: '20px', resize: 'vertical' }}
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Typ hier je bericht..."
                      required
                    />
                    <button type="submit" className="btn" style={{ width: '100%', minWidth: 'auto' }} disabled={isSubmittingFeedback}>
                      {isSubmittingFeedback ? "Versturen..." : "Feedback Versturen"}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}
      </SignedIn>

      <SignedOut>
        <h1 className="aharoni-style" style={{ fontSize: '2.5em', marginBottom: '40px' }}>Inloggen Instructeur</h1>
        
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <SignIn routing="hash" appearance={clerkAppearance} forceRedirectUrl="/admin/login" />
        </div>
        
        <div className="admin-card" style={{ width: '100%', maxWidth: '450px', padding: '20px', marginTop: '20px', textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #333', paddingTop: '20px', width: '100%' }}>
            <p style={{ color: '#888', marginBottom: '10px' }}>Nog geen account?</p>
            <Link to="/register" className="register-link" style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
              Nu Registreren
            </Link>
          </div>
        </div>
      </SignedOut>
    </div>
  );
};

export default AdminLogin;
